import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
  Checkbox,
  FormHelperText,
  Divider,
  ListItemText,
  FormGroup,
  Chip,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';

const PERCENT_FORMATTER = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const NUMBER_FORMATTER = new Intl.NumberFormat('es-CO');

const CHART_COLORS = [
  '#667eea', '#9c27b0', '#ff9800', '#2e7d32', '#d81b60',
  '#00897b', '#c2185b', '#5d4037', '#f57f17', '#455a64',
  '#7b1fa2', '#388e3c', '#f4511e', '#1e88e5', '#6d4c41'
];

const CHART_TYPES = [
  { value: 'pie', label: 'Circular' },
  { value: 'bar', label: 'Barras verticales' },
  { value: 'bar-horizontal', label: 'Barras horizontales' }
];

const FILTER_CONTROL_SX = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    fontSize: '0.9rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem'
  },
  '& .MuiSelect-select': {
    py: 0.4
  }
};

const FILTER_LABEL_SX = {
  '& .MuiFormControlLabel-label': {
    fontSize: '0.85rem'
  }
};

const ResponsiveGridLayout = WidthProvider(Responsive);

const MIN_REFRESH_INTERVAL = 15000; // 15 segundos mínimo
const DEFAULT_REFRESH_INTERVAL = 60000; // 60 segundos por defecto
const FILTERS_STORAGE_KEY = 'supervisorQueuesFilters';
const REFRESH_INTERVAL_STORAGE_KEY = 'supervisorQueuesRefreshInterval';

const buildFiltersSignature = ({ campaignId = null, skillDefs = [], loadIds = [], endings = [] } = {}) => {
  const normalizedCampaign = campaignId === undefined || campaignId === null || campaignId === ''
    ? null
    : String(campaignId);
  const normalizedSkills = [...(skillDefs || [])]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .sort();
  const normalizedLoads = [...(loadIds || [])]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .sort();
  const normalizedEndings = [...(endings || [])]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .sort();
  return JSON.stringify({
    campaignId: normalizedCampaign,
    skillDefs: normalizedSkills,
    loadIds: normalizedLoads,
    endings: normalizedEndings
  });
};

const buildMetricState = (currentValue = 0, targetValue = 0, totalTicks = 1) => {
  const diff = targetValue - currentValue;
  return {
    target: targetValue,
    direction: diff >= 0 ? 1 : -1,
    remaining: Math.abs(diff),
    accumulator: 0,
    step: Math.abs(diff) / Math.max(totalTicks, 1)
  };
};

const ANIMATION_MIN_INTERVAL_MS = 50;
const ANIMATION_MAX_INTERVAL_MS = 250;

const DEFAULT_LAYOUTS = {
  lg: [
    { i: 'table', x: 0, y: 0, w: 6, h: 20, minW: 4, minH: 10 },
    { i: 'chart', x: 6, y: 0, w: 6, h: 20, minW: 4, minH: 10 }
  ],
  md: [
    { i: 'table', x: 0, y: 0, w: 5, h: 20, minW: 4, minH: 10 },
    { i: 'chart', x: 5, y: 0, w: 5, h: 20, minW: 4, minH: 10 }
  ],
  sm: [
    { i: 'table', x: 0, y: 0, w: 8, h: 20, minW: 4, minH: 10 },
    { i: 'chart', x: 0, y: 20, w: 8, h: 20, minW: 4, minH: 10 }
  ],
  xs: [
    { i: 'table', x: 0, y: 0, w: 6, h: 20, minW: 4, minH: 10 },
    { i: 'chart', x: 0, y: 20, w: 6, h: 20, minW: 4, minH: 10 }
  ],
  xxs: [
    { i: 'table', x: 0, y: 0, w: 4, h: 20, minW: 4, minH: 10 },
    { i: 'chart', x: 0, y: 20, w: 4, h: 20, minW: 4, minH: 10 }
  ]
};

const computeGroupLabel = (group, level, emptyLabel = 'Sin final') => {
  if (!group || !Array.isArray(group.parts) || group.parts.length === 0) {
    return emptyLabel;
  }
  if (!level || level <= 0 || level >= group.parts.length) {
    return group.label || group.parts.join(' / ');
  }
  return group.parts.slice(0, level).join(' / ');
};

const aggregateSkillRows = (skills = [], level = 0, emptyLabel = 'Sin final') => {
  const buckets = new Map();
  skills.forEach((skill) => {
    const skillTotal = skill.totalRecords || 0;
    (skill.groups || []).forEach((group) => {
      const label = computeGroupLabel(group, level, emptyLabel);
      const current = buckets.get(label) || { label, total: 0, percentage: 0 };
      current.total += group.total;
      current.percentage += skillTotal > 0 ? (group.total / skillTotal) * 100 : 0;
      buckets.set(label, current);
    });
  });
  return Array.from(buckets.values()).sort((a, b) => b.total - a.total);
};

const aggregateChartData = (rows = []) => rows.slice(0, 12).map((row) => ({
  label: row.label,
  total: row.total,
  percentage: row.percentage ?? 0
}));

const buildChartEntries = (rows = []) => rows.slice(0, 12).map((row, index) => ({
  id: row.label || `Final ${index + 1}`,
  label: row.label || `Final ${index + 1}`,
  value: row.total,
  percentage: row.percentage ?? 0,
  color: CHART_COLORS[index % CHART_COLORS.length]
}));

const formatPercentage = (value = 0) => `${PERCENT_FORMATTER.format(value)} %`;
const RADIAN = Math.PI / 180;

const renderPieLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, payload
}) => {
  const percentageValue = payload?.stablePercentage ?? payload?.percentage ?? 0;
  if (percentageValue <= 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const value = formatPercentage(percentageValue);
  return (
    <text
      x={x}
      y={y}
      fill="#1f2933"
      fontSize={11}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {value}
    </text>
  );
};

export default function SupervisorQueuesPage() {
  const { supervisor, campaigns, skills, loads, sse } = useApi();
  const { showError } = useNotification();

  const [campaignList, setCampaignList] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLoads, setSelectedLoads] = useState([]);
  const [selectedEndings, setSelectedEndings] = useState([]);
  const [chartType, setChartType] = useState('pie');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [silentRefresh, setSilentRefresh] = useState(false);
  const [data, setData] = useState(null);
  const [loadsList, setLoadsList] = useState([]);
  const [emptyValue, setEmptyValue] = useState('__EMPTY__');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ttlMs, setTtlMs] = useState(MIN_REFRESH_INTERVAL);
  const [cachedResult, setCachedResult] = useState(false);
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [visiblePanels, setVisiblePanels] = useState({ table: true, chart: true });
  const [displayedAggregatedRows, setDisplayedAggregatedRows] = useState([]);
  const [displayedTotalRecords, setDisplayedTotalRecords] = useState(0);
  const [customRefreshInterval, setCustomRefreshInterval] = useState(DEFAULT_REFRESH_INTERVAL);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  const animationRef = useRef(null);
  const displayedRowsRef = useRef([]);
  const displayedTotalRef = useRef(0);
  const previousFiltersSignatureRef = useRef(null);
  const isManualRefreshRef = useRef(false);
  const shouldAnimateRef = useRef(true);

  const refreshIntervalMs = useMemo(
    () => Math.max(customRefreshInterval || MIN_REFRESH_INTERVAL, MIN_REFRESH_INTERVAL),
    [customRefreshInterval]
  );

  const loadCatalogs = useCallback(async () => {
    try {
      const [campaignsRes, skillsRes] = await Promise.all([
        campaigns.getAll(),
        skills.getAll()
      ]);
      setCampaignList(campaignsRes?.data?.campaigns || []);
      setSkillsList(skillsRes?.data?.skills || []);
    } catch (error) {
      console.error('[SupervisorQueues] Error cargando catálogos', error);
      showError(error.message || 'No se pudieron cargar los catálogos');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skillDefById = useMemo(() => {
    const map = new Map();
    (skillsList || []).forEach((skill) => {
      map.set(skill.id, skill.skilldef);
    });
    return map;
  }, [skillsList]);

  const filteredSkills = useMemo(() => {
    if (!selectedCampaign) return skillsList;
    return skillsList.filter((item) => String(item.idcampaign || '') === String(selectedCampaign));
  }, [skillsList, selectedCampaign]);

  const requestSkillDefs = useMemo(
    () => selectedSkills.filter((value) => value !== undefined && value !== null && value !== ''),
    [selectedSkills]
  );

  const requestLoadIds = useMemo(
    () => selectedLoads.filter((value) => value !== undefined && value !== null && value !== ''),
    [selectedLoads]
  );

  const filteredLoads = useMemo(() => {
    let list = loadsList;
    if (selectedCampaign) {
      const allowedSkillIds = new Set(
        skillsList
          .filter((item) => String(item.idcampaign || '') === String(selectedCampaign))
          .map((item) => item.id)
      );
      list = list.filter((load) => allowedSkillIds.has(load.skill_id));
    }
    if (requestSkillDefs.length > 0) {
      const allowedSkillDefs = new Set(requestSkillDefs);
      list = list.filter((load) => allowedSkillDefs.has(skillDefById.get(load.skill_id)));
    }
    return list;
  }, [loadsList, selectedCampaign, requestSkillDefs, skillsList, skillDefById]);

  // Cargar filtros e intervalo desde localStorage al iniciar
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.campaignId !== undefined) setSelectedCampaign(filters.campaignId || '');
        if (filters.skillDefs && Array.isArray(filters.skillDefs)) setSelectedSkills(filters.skillDefs);
        if (filters.loadIds && Array.isArray(filters.loadIds)) setSelectedLoads(filters.loadIds);
        if (filters.endings && Array.isArray(filters.endings)) setSelectedEndings(filters.endings);
      }

      const savedInterval = localStorage.getItem(REFRESH_INTERVAL_STORAGE_KEY);
      if (savedInterval) {
        const interval = parseInt(savedInterval, 10);
        if (!isNaN(interval) && interval >= MIN_REFRESH_INTERVAL) {
          setCustomRefreshInterval(interval);
        }
      }
    } catch (error) {
      console.error('[SupervisorQueues] Error cargando configuración desde localStorage', error);
    } finally {
      // Marcar que los filtros ya se cargaron
      setFiltersLoaded(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await loadCatalogs();
      try {
        const response = await loads.getAll();
        setLoadsList(response?.data?.loads || []);
      } catch (error) {
        console.error('[SupervisorQueues] Error cargando cargas', error);
        showError(error.message || 'No se pudieron cargar las cargas');
      }
      // Marcar que los catálogos ya se cargaron
      setCatalogsLoaded(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Limpiar skills seleccionados que no pertenecen al filtro actual
    // Solo después de que los catálogos se hayan cargado
    if (!catalogsLoaded) {
      return;
    }

    setSelectedSkills((prev) => prev.filter((skillDef) =>
      filteredSkills.some((item) => item.skilldef === skillDef)
    ));
  }, [filteredSkills, catalogsLoaded]);

  useEffect(() => {
    // Limpiar cargas seleccionadas que no pertenecen al filtro actual
    // Solo después de que los catálogos se hayan cargado
    if (!catalogsLoaded) {
      return;
    }

    setSelectedLoads((prev) => prev.filter((loadId) =>
      filteredLoads.some((load) => String(load.id) === String(loadId))
    ));
  }, [filteredLoads, catalogsLoaded]);

  useEffect(() => {
    displayedRowsRef.current = displayedAggregatedRows;
  }, [displayedAggregatedRows]);

  useEffect(() => {
    displayedTotalRef.current = displayedTotalRecords;
  }, [displayedTotalRecords]);

  useEffect(() => () => {
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const currentFiltersSignature = useMemo(() => buildFiltersSignature({
    campaignId: selectedCampaign || null,
    skillDefs: requestSkillDefs,
    loadIds: requestLoadIds,
    endings: selectedEndings
  }), [selectedCampaign, requestSkillDefs, requestLoadIds, selectedEndings]);

  const handleMetricsPayload = useCallback((payload = {}) => {
    setData(payload || null);
    setEmptyValue(payload?.emptyValue || '__EMPTY__');
    setLastUpdated(payload?.generatedAt || new Date().toISOString());
    setTtlMs(payload?.ttlMs || MIN_REFRESH_INTERVAL);
    setCachedResult(Boolean(payload?.cached));
  }, []);

  const animateAggregatedTransition = useCallback((nextRows = [], nextTotal = 0, durationMs = MIN_REFRESH_INTERVAL, shouldAnimate = true) => {
    if (!nextRows.length) {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      setDisplayedAggregatedRows([]);
      setDisplayedTotalRecords(nextTotal || 0);
      return;
    }

    const previousRows = displayedRowsRef.current;
    const previousTotal = displayedTotalRef.current;

    if (!previousRows.length || !shouldAnimate) {
      setDisplayedAggregatedRows(nextRows);
      setDisplayedTotalRecords(nextTotal);
      return;
    }

    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }

    const previousMap = new Map(previousRows.map((row) => [row.label, row]));
    const targetRows = nextRows.map((row) => ({ ...row }));

    let workingRows = targetRows.map((row) => {
      const previous = previousMap.get(row.label);
      return {
        ...row,
        total: previous ? previous.total ?? 0 : 0,
        percentage: row.percentage ?? 0
      };
    });

    let workingTotal = previousTotal ?? 0;

    const maxRowDiff = nextRows.reduce((acc, row) => {
      const previous = previousMap.get(row.label);
      const totalDiffValue = Math.abs((row.total ?? 0) - (previous?.total ?? 0));
      const percentageDiffValue = Math.abs((row.percentage ?? 0) - (previous?.percentage ?? 0));
      return Math.max(acc, totalDiffValue, percentageDiffValue);
    }, 0);
    const totalDiff = Math.abs((nextTotal ?? 0) - workingTotal);
    const maxDiff = Math.max(maxRowDiff, totalDiff);

    if (!maxDiff) {
      setDisplayedAggregatedRows(nextRows);
      setDisplayedTotalRecords(nextTotal);
      return;
    }

    const effectiveDuration = Math.max(durationMs || MIN_REFRESH_INTERVAL, MIN_REFRESH_INTERVAL);
    const baseDelay = effectiveDuration / Math.max(maxDiff, 1);
    const stepDelay = Math.max(
      ANIMATION_MIN_INTERVAL_MS,
      Math.min(ANIMATION_MAX_INTERVAL_MS, baseDelay)
    );
    const totalTicks = Math.max(Math.ceil(effectiveDuration / stepDelay), 1);

    const rowStates = nextRows.map((row) => {
      const previous = previousMap.get(row.label) || {};
      return {
        total: buildMetricState(previous.total ?? 0, row.total ?? 0, totalTicks)
      };
    });
    const totalState = buildMetricState(workingTotal, nextTotal ?? 0, totalTicks);

    let ticksElapsed = 0;

    animationRef.current = setInterval(() => {
      ticksElapsed += 1;
      const forceCompletion = ticksElapsed >= totalTicks;
      let hasPending = false;
      let producedChange = false;

      workingRows = workingRows.map((current, index) => {
        const state = rowStates[index]?.total;
        const targetRow = targetRows[index];
        if (!state) {
          return {
            ...targetRow,
            total: targetRow.total ?? 0,
            percentage: targetRow.percentage ?? 0
          };
        }

        const updatedRow = { ...current, percentage: targetRow.percentage ?? 0 };

        if (state.remaining <= 0) {
          updatedRow.total = state.target;
        } else {
          state.accumulator += state.step || 0;
          const pendingSteps = forceCompletion
            ? state.remaining
            : Math.min(state.remaining, Math.floor(state.accumulator));
          if (pendingSteps > 0) {
            updatedRow.total = (updatedRow.total ?? 0) + (state.direction * pendingSteps);
            state.remaining -= pendingSteps;
            state.accumulator -= pendingSteps;
            producedChange = true;
          }

          if (state.remaining <= 0) {
            updatedRow.total = state.target;
          } else {
            hasPending = true;
          }
        }

        return updatedRow;
      });

      if (totalState && totalState.remaining > 0) {
        totalState.accumulator += totalState.step || 0;
        const pendingTotal = forceCompletion
          ? totalState.remaining
          : Math.min(totalState.remaining, Math.floor(totalState.accumulator));
        if (pendingTotal > 0) {
          workingTotal += totalState.direction * pendingTotal;
          totalState.remaining -= pendingTotal;
          totalState.accumulator -= pendingTotal;
          producedChange = true;
        }
        if (totalState.remaining <= 0) {
          workingTotal = totalState.target;
        } else {
          hasPending = true;
        }
      } else if (totalState && totalState.remaining <= 0) {
        workingTotal = totalState.target;
      }

      setDisplayedAggregatedRows(workingRows);
      setDisplayedTotalRecords(workingTotal);

      if (!hasPending) {
        clearInterval(animationRef.current);
        animationRef.current = null;
        setDisplayedAggregatedRows(nextRows);
        setDisplayedTotalRecords(nextTotal);
      }
    }, stepDelay);

  }, []);

  const fetchMetrics = useCallback(async ({ showSpinner = true, forceRefresh = false } = {}) => {
    if (showSpinner) {
      setLoading(true);
    } else {
      setSilentRefresh(true);
    }

    try {
      const params = {};
      if (selectedCampaign) params.campaignId = selectedCampaign;
      if (requestSkillDefs.length > 0) params.skillDefs = requestSkillDefs;
      if (requestLoadIds.length > 0) params.loadIds = requestLoadIds;
      if (forceRefresh) params.forceRefresh = true;

      const response = await supervisor.getQueueMetrics(params);
      handleMetricsPayload(response?.data || {});
    } catch (error) {
      console.error('[SupervisorQueues] Error obteniendo métricas', error);
      showError(error.message || 'No se pudieron obtener las métricas');
    } finally {
      if (showSpinner) {
        setLoading(false);
      } else {
        setSilentRefresh(false);
      }
    }
  }, [supervisor, selectedCampaign, requestSkillDefs, requestLoadIds, showError, handleMetricsPayload]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!sse || !autoRefresh) {
      return undefined;
    }

    const handler = (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        const payloadKey = payload.filtersKey || buildFiltersSignature(payload.filters || {});
        if (payloadKey !== currentFiltersSignature) {
          return;
        }
        handleMetricsPayload(payload);
      } catch (error) {
        console.error('[SupervisorQueues] Error processing SSE queue metrics event', error);
      }
    };

    sse.addEventListener('queue-metrics', handler);

    return () => {
      sse.removeEventListener('queue-metrics', handler);
    };
  }, [autoRefresh, sse, currentFiltersSignature, handleMetricsPayload]);

  useEffect(() => {
    if (autoRefresh && sse) {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchMetrics({ showSpinner: false });
    }, refreshIntervalMs);

    return () => clearInterval(interval);
  }, [autoRefresh, sse, refreshIntervalMs, fetchMetrics]);

  const handleManualRefresh = useCallback(() => {
    // Marcar que es un refresh manual para evitar animación
    isManualRefreshRef.current = true;

    if (autoRefresh && sse) {
      supervisor
        .refreshQueueMetrics({
          campaignId: selectedCampaign || null,
          skillDefs: requestSkillDefs,
          loadIds: requestLoadIds
        })
        .catch((error) => {
          console.error('[SupervisorQueues] Error forcing queue metrics refresh', error);
          showError(error.message || 'Error al refrescar métricas');
          fetchMetrics({ showSpinner: false, forceRefresh: true });
        });
      return;
    }
    fetchMetrics({ showSpinner: false, forceRefresh: true });
  }, [
    autoRefresh,
    sse,
    supervisor,
    selectedCampaign,
    requestSkillDefs,
    requestLoadIds,
    fetchMetrics,
    showError
  ]);

  const maxLevel = useMemo(() => {
    if (!data?.skills?.length) return 0;
    return data.skills.reduce((acc, skill) => Math.max(acc, skill.maxLevel || 0), 0);
  }, [data]);

  useEffect(() => {
    if (selectedLevel > maxLevel) {
      setSelectedLevel(maxLevel);
    }
  }, [maxLevel, selectedLevel]);

  const aggregatedRows = useMemo(() => {
    if (!data?.skills?.length) return [];
    return aggregateSkillRows(data.skills, selectedLevel, emptyValue);
  }, [data, selectedLevel, emptyValue]);

  // Lista de finales únicos disponibles en los datos actuales
  const availableEndings = useMemo(() => {
    if (!aggregatedRows.length) return [];
    return aggregatedRows.map((row) => row.label).sort();
  }, [aggregatedRows]);

  useEffect(() => {
    // Limpiar finales seleccionados que ya no están disponibles
    // Y seleccionar todos por defecto si no hay ninguno seleccionado
    if (!catalogsLoaded || !filtersLoaded) {
      return;
    }

    setSelectedEndings((prev) => {
      const filtered = prev.filter((ending) => availableEndings.includes(ending));
      // Si no hay finales seleccionados y hay finales disponibles, seleccionar todos
      if (filtered.length === 0 && availableEndings.length > 0) {
        return availableEndings;
      }
      return filtered;
    });
  }, [availableEndings, catalogsLoaded, filtersLoaded]);

  // Filtrar las filas según los finales seleccionados y recalcular porcentajes
  const filteredAggregatedRows = useMemo(() => {
    if (!selectedEndings.length) return aggregatedRows;

    const filtered = aggregatedRows.filter((row) => selectedEndings.includes(row.label));
    const filteredTotal = filtered.reduce((acc, row) => acc + row.total, 0);

    // Recalcular porcentajes basados en el total filtrado
    return filtered.map((row) => ({
      ...row,
      percentage: filteredTotal > 0 ? (row.total / filteredTotal) * 100 : 0
    }));
  }, [aggregatedRows, selectedEndings]);

  const stablePercentages = useMemo(() => {
    const map = new Map();
    filteredAggregatedRows.forEach((row) => {
      map.set(row.label, row.percentage ?? 0);
    });
    return map;
  }, [filteredAggregatedRows]);

  const totalRecords = filteredAggregatedRows.reduce((acc, row) => acc + row.total, 0);

  // Detectar cambios de filtros ANTES de que cambien los datos
  useEffect(() => {
    if (previousFiltersSignatureRef.current === null) {
      // Primera carga
      previousFiltersSignatureRef.current = currentFiltersSignature;
      shouldAnimateRef.current = true;
      return;
    }

    const filtersChanged = previousFiltersSignatureRef.current !== currentFiltersSignature;

    if (filtersChanged) {
      // Los filtros cambiaron, no animar
      shouldAnimateRef.current = false;
      previousFiltersSignatureRef.current = currentFiltersSignature;
    } else {
      // Los filtros no cambiaron, permitir animación (a menos que sea refresh manual)
      shouldAnimateRef.current = !isManualRefreshRef.current;
    }
  }, [currentFiltersSignature]);

  // Animar cuando cambien los datos agregados
  useEffect(() => {
    const shouldAnimate = shouldAnimateRef.current && !isManualRefreshRef.current;

    animateAggregatedTransition(filteredAggregatedRows, totalRecords, ttlMs, shouldAnimate);

    // Resetear flags después de animar
    if (isManualRefreshRef.current) {
      isManualRefreshRef.current = false;
    }

    // Para la siguiente actualización, permitir animación por defecto
    shouldAnimateRef.current = true;
  }, [filteredAggregatedRows, totalRecords, ttlMs, animateAggregatedTransition]);

  const rowsForDisplay = displayedAggregatedRows.length > 0 ? displayedAggregatedRows : filteredAggregatedRows;
  const totalRecordsForDisplay = displayedTotalRecords || totalRecords;

  const pieChartData = useMemo(
    () => buildChartEntries(rowsForDisplay).map((row) => ({
      ...row,
      stablePercentage: stablePercentages.get(row.label) ?? row.percentage ?? 0
    })),
    [rowsForDisplay, stablePercentages]
  );

  const barChartData = useMemo(
    () => buildChartEntries(rowsForDisplay).map((row) => ({
      ...row,
      stablePercentage: stablePercentages.get(row.label) ?? row.percentage ?? 0
    })),
    [rowsForDisplay, stablePercentages]
  );

  const levelOptions = useMemo(() => {
    const options = [{ label: 'Detalle completo', value: 0 }];
    for (let i = 1; i <= maxLevel; i += 1) {
      options.push({ label: `Nivel ${i}`, value: i });
    }
    return options;
  }, [maxLevel]);

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value);
  };

  const handleSkillsChange = (event) => {
    const value = event.target.value;
    setSelectedSkills(Array.isArray(value) ? value : []);
  };

  const handleLoadsChange = (event) => {
    const value = event.target.value;
    setSelectedLoads(Array.isArray(value) ? value : []);
  };

  const handleEndingsChange = (event) => {
    const value = event.target.value;
    setSelectedEndings(Array.isArray(value) ? value : []);
  };

  const handleChartTypeChange = (event) => {
    setChartType(event.target.value);
  };

  const handleLevelChange = (event) => {
    setSelectedLevel(Number(event.target.value));
  };

  // Guardar filtros en localStorage cuando cambien (solo después de la carga inicial)
  useEffect(() => {
    if (!filtersLoaded) {
      return;
    }

    const filters = {
      campaignId: selectedCampaign,
      skillDefs: selectedSkills,
      loadIds: selectedLoads,
      endings: selectedEndings
    };

    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
      console.log('[SupervisorQueues] Filtros guardados:', filters);
    } catch (error) {
      console.error('[SupervisorQueues] Error guardando filtros en localStorage', error);
    }
  }, [selectedCampaign, selectedSkills, selectedLoads, selectedEndings, filtersLoaded]);

  // Guardar intervalo personalizado en localStorage cuando cambie (solo después de la carga inicial)
  useEffect(() => {
    if (!filtersLoaded) {
      return;
    }

    try {
      localStorage.setItem(REFRESH_INTERVAL_STORAGE_KEY, String(customRefreshInterval));
      console.log('[SupervisorQueues] Intervalo guardado:', customRefreshInterval);
    } catch (error) {
      console.error('[SupervisorQueues] Error guardando intervalo en localStorage', error);
    }
  }, [customRefreshInterval, filtersLoaded]);

  const handlePanelToggle = (panel) => (event) => {
    const checked = event.target.checked;
    setVisiblePanels((prev) => ({
      ...prev,
      [panel]: checked
    }));
  };

  const handleLayoutChange = useCallback((_, allLayouts) => {
    setLayouts(allLayouts);
  }, []);

const renderLegendPanel = (items = [], title = 'Principales finales') => (
  <Box sx={{ width: '100%', mt: 2 }}>
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        width: '100%',
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr' },
          gap: 1.25
        }}
      >
        {items.map((item, index) => (
          <Stack key={item.label} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '2px',
                mt: '6px',
                bgcolor: CHART_COLORS[index % CHART_COLORS.length]
              }}
            />
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {item.label} - {NUMBER_FORMATTER.format(item.total)} ({formatPercentage(item.percentage)})
            </Typography>
          </Stack>
        ))}
      </Box>
    </Paper>
  </Box>
);

  const renderPieTooltip = useCallback(({ datum }) => (
    <Paper elevation={0} sx={{ p: 1.2 }}>
      <Typography variant="subtitle2">{datum.label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {NUMBER_FORMATTER.format(datum.value)} ({formatPercentage(datum.data.stablePercentage ?? datum.data.percentage ?? 0)})
      </Typography>
    </Paper>
  ), []);

  const legendRows = rowsForDisplay.length > 0 ? rowsForDisplay : aggregatedRows;

  const renderChart = () => {
    const hasData = chartType === 'pie' ? pieChartData.length > 0 : barChartData.length > 0;

    if (!hasData) {
      return (
        <Box py={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No hay datos suficientes para el gráfico.
          </Typography>
        </Box>
      );
    }

    if (chartType === 'pie') {
      return (
        <Stack spacing={2} alignItems="stretch">
          <Box
            sx={{
              flex: 1,
              minHeight: 320,
              maxHeight: '100%',
              overflow: 'hidden'
            }}
          >
            <ResponsivePie
              data={pieChartData}
              valueFormat={(value) => NUMBER_FORMATTER.format(value)}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              innerRadius={0.6}
              padAngle={1.5}
              cornerRadius={3}
              colors={{ datum: 'data.color' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              activeOuterRadiusOffset={8}
              arcLabelsSkipAngle={5}
              arcLabel={(d) => formatPercentage(d.data.stablePercentage ?? d.data.percentage ?? 0)}
              enableArcLinkLabels={false}
              tooltip={renderPieTooltip}
              animate
              motionConfig="gentle"
            />
          </Box>
          {renderLegendPanel(legendRows)}
        </Stack>
      );
    }

    return (
      <Stack spacing={2} alignItems="stretch">
        <Box sx={{ flex: 1, minHeight: 320 }}>
          <ResponsiveBar
            data={barChartData}
            keys={['value']}
            indexBy="label"
            margin={{ top: 20, right: 20, bottom: 20, left: chartType === 'bar-horizontal' ? 120 : 40 }}
            padding={0.25}
            layout={chartType === 'bar-horizontal' ? 'horizontal' : 'vertical'}
            colors={({ index }) => CHART_COLORS[index % CHART_COLORS.length]}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisBottom={chartType === 'bar-horizontal' ? null : undefined}
            enableLabel={false}
            animate
            motionConfig="gentle"
            tooltip={({ id, value, color, data }) => (
              <Paper elevation={0} sx={{ p: 1.2, color: '#1f2933' }}>
                <Typography variant="subtitle2">{id}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {NUMBER_FORMATTER.format(value)} ({formatPercentage(data.stablePercentage ?? data.percentage ?? 0)})
                </Typography>
              </Paper>
            )}
          />
        </Box>
        {renderLegendPanel(legendRows)}
      </Stack>
    );
  };
  const filteredSkillsForSelect = filteredSkills.map((skill) => ({
    value: skill.skilldef,
    label: skill.skillname
  }));

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs>
              <Typography variant="h6" fontSize="1rem">
                Filtros
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Intervalo (seg)"
                  type="number"
                  size="small"
                  value={customRefreshInterval / 1000}
                  onChange={(event) => {
                    const seconds = Number(event.target.value);
                    const ms = seconds * 1000;
                    if (ms >= MIN_REFRESH_INTERVAL) {
                      setCustomRefreshInterval(ms);
                    }
                  }}
                  inputProps={{
                    min: MIN_REFRESH_INTERVAL / 1000,
                    step: 5
                  }}
                  sx={{
                    width: 120,
                    '& .MuiInputBase-root': {
                      fontSize: '0.9rem'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={autoRefresh}
                      onChange={(event) => setAutoRefresh(event.target.checked)}
                      size="small"
                    />
                  )}
                  label="Auto refrescar"
                  sx={FILTER_LABEL_SX}
                />
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleManualRefresh}
                  disabled={loading || silentRefresh}
                  size="small"
                >
                  Actualizar
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={1} alignItems="center" sx={{ fontSize: '0.85rem' }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="campaign-filter-label">Campaña</InputLabel>
                <Select
                  labelId="campaign-filter-label"
                  value={selectedCampaign}
                  label="Campaña"
                  onChange={handleCampaignChange}
                >
                  <MenuItem value="">Todas las campañas</MenuItem>
                  {campaignList.map((campaign) => (
                    <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                      {campaign.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="skills-filter-label">Skills</InputLabel>
                <Select
                  labelId="skills-filter-label"
                  multiple
                  value={selectedSkills}
                  label="Skills"
                  onChange={handleSkillsChange}
                  renderValue={(selected) => {
                    if (!selected.length) return 'Todos los skills';
                    return selected.join(', ');
                  }}
                >
                  {filteredSkillsForSelect.map((skill) => (
                    <MenuItem key={skill.value} value={skill.value}>
                        <Checkbox checked={selectedSkills.includes(skill.value)} />
                      <ListItemText primary={skill.label} />
                    </MenuItem>
                  ))}
                  {filteredSkillsForSelect.length === 0 && (
                    <MenuItem disabled>
                      <ListItemText primary="Sin skills para la campaña seleccionada" />
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="loads-filter-label">Cargas</InputLabel>
                <Select
                  labelId="loads-filter-label"
                  multiple
                  value={selectedLoads}
                  label="Cargas"
                  onChange={handleLoadsChange}
                  renderValue={(selected) => {
                    if (!selected.length) return 'Todas las cargas';
                    return selected.map((id) => {
                      const item = loadsList.find((load) => String(load.id) === String(id));
                      return item?.name || `Carga ${id}`;
                    }).join(', ');
                  }}
                >
                  {filteredLoads.map((load) => {
                    const loadDef = skillDefById.get(load.skill_id) || load.skill_def;
                    return (
                      <MenuItem key={load.id} value={load.id}>
                        <Checkbox checked={selectedLoads.includes(load.id)} />
                        <ListItemText
                          primary={load.name || `Carga ${load.id}`}
                          secondary={loadDef || ''}
                        />
                      </MenuItem>
                    );
                  })}
                  {filteredLoads.length === 0 && (
                    <MenuItem disabled>
                      <ListItemText primary="Sin cargas disponibles" />
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="level-filter-label">Nivel</InputLabel>
                <Select
                  labelId="level-filter-label"
                  value={selectedLevel}
                  label="Nivel"
                  onChange={handleLevelChange}
                >
                  {levelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1} alignItems="center" sx={{ fontSize: '0.85rem', mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="endings-filter-label">Finales</InputLabel>
                <Select
                  labelId="endings-filter-label"
                  multiple
                  value={selectedEndings}
                  label="Finales"
                  onChange={handleEndingsChange}
                  renderValue={(selected) => {
                    if (!selected.length) return 'Todos los finales';
                    if (selected.length <= 3) {
                      return selected.join(', ');
                    }
                    return `${selected.length} finales seleccionados`;
                  }}
                >
                  {availableEndings.map((ending) => (
                    <MenuItem key={ending} value={ending}>
                      <Checkbox checked={selectedEndings.includes(ending)} />
                      <ListItemText primary={ending} />
                    </MenuItem>
                  ))}
                  {availableEndings.length === 0 && (
                    <MenuItem disabled>
                      <ListItemText primary="Sin finales disponibles" />
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>Selecciona los finales que deseas visualizar en el gráfico y la tabla</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <FormControl fullWidth size="small" sx={{ maxWidth: 200, ...FILTER_CONTROL_SX }}>
                  <InputLabel id="chart-type-label">Tipo de gráfico</InputLabel>
                  <Select
                    labelId="chart-type-label"
                    value={chartType}
                    label="Tipo de gráfico"
                    onChange={handleChartTypeChange}
                    disabled={!visiblePanels.chart}
                  >
                    {CHART_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormGroup row sx={{ alignItems: 'center' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={visiblePanels.table}
                        onChange={handlePanelToggle('table')}
                      />
                    )}
                    label="Mostrar tabla"
                    sx={FILTER_LABEL_SX}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={visiblePanels.chart}
                        onChange={handlePanelToggle('chart')}
                      />
                    )}
                    label="Mostrar gráfico"
                    sx={FILTER_LABEL_SX}
                  />
                </FormGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Última actualización:
                </Typography>
                <Typography variant="body2">
                  {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Sin datos'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                <Chip
                  size="small"
                  label={cachedResult ? 'Desde caché' : 'Refresco reciente'}
                  color={cachedResult ? 'default' : 'success'}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Intervalo ~${Math.round(refreshIntervalMs / 1000)}s`}
                  variant="outlined"
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        <Box sx={{ height: 4 }}>
          {(loading || silentRefresh) && <LinearProgress />}
        </Box>
      </Card>

      <Box mt={2}>
        <ResponsiveGridLayout
          className="supervision-queues-layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 8, xs: 6, xxs: 4 }}
          rowHeight={30}
          margin={[16, 16]}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
        >
          {visiblePanels.table && (
            <Box key="table" sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <Box
                  className="drag-handle"
                  sx={{ px: 2, py: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                  <Typography variant="subtitle2">Tabla de finales</Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {rowsForDisplay.length === 0 ? (
                    <Box py={6} textAlign="center">
              <Typography variant="subtitle1" fontWeight={500}>
                No hay finales para mostrar
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ajusta los filtros para visualizar información.
                      </Typography>
                    </Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Final</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">% acumulado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rowsForDisplay.map((row) => (
                          <TableRow key={row.label}>
                            <TableCell>
                              <Typography variant="body2">{row.label}</Typography>
                            </TableCell>
                            <TableCell align="right">{NUMBER_FORMATTER.format(row.total)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title={`${row.total} registros`}>
                                <span>{formatPercentage(row.percentage)}</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'action.hover', fontWeight: 'bold' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {NUMBER_FORMATTER.format(totalRecordsForDisplay)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatPercentage(100)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Paper>
            </Box>
          )}

          {visiblePanels.chart && (
            <Box key="chart" sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2
                }}
              >
                <Typography variant="subtitle2" className="drag-handle" sx={{ mb: 2, cursor: 'move' }}>
                  Visualización de finales
                </Typography>
                {renderChart()}
              </Paper>
            </Box>
          )}
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
}



